import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Upload,
  Sparkles,
  RefreshCw,
  CheckCircle2,
  Info,
  BookOpen,
  Loader2,
  ImageIcon,
  ChevronDown,
  Maximize2,
  Download,
  Check,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';
import { api, AiLimitError } from '../lib/api';
import { notify } from '../lib/toast';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalFooter,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { cn } from '../lib/utils';

// ─── Cover size presets ───────────────────────────────────────────────────────

interface SizePreset {
  id: string;
  label: string;
  shortLabel: string;
  width: number;
  height: number;
  dpi?: number;
  recommended?: boolean;
  description: string;
  category: 'ebook' | 'print';
}

const SIZE_PRESETS: SizePreset[] = [
  {
    id: 'kdp-ebook',
    label: 'Ebook Amazon KDP',
    shortLabel: 'KDP',
    width: 1600,
    height: 2560,
    recommended: true,
    description: 'Recomendado para Kindle. Ratio 5:8.',
    category: 'ebook',
  },
  {
    id: 'ebook-2-3',
    label: 'Ebook 2:3',
    shortLabel: '2:3',
    width: 1600,
    height: 2400,
    description: 'Proporção clássica. Kobo, Apple Books, Smashwords.',
    category: 'ebook',
  },
  {
    id: 'print-6x9',
    label: 'Print 6×9"',
    shortLabel: '6×9"',
    width: 1800,
    height: 2700,
    dpi: 300,
    description: 'Mais popular para impressão. Romance, não-ficção.',
    category: 'print',
  },
  {
    id: 'print-5x8',
    label: 'Print 5×8"',
    shortLabel: '5×8"',
    width: 1500,
    height: 2400,
    dpi: 300,
    description: 'Formato bolso e YA. Ratio 5:8.',
    category: 'print',
  },
  {
    id: 'print-5-5x8-5',
    label: 'Print 5.5×8.5"',
    shortLabel: '5.5×8.5"',
    width: 1650,
    height: 2550,
    dpi: 300,
    description: 'Intermediário. Muito usado em não-ficção.',
    category: 'print',
  },
];

// AI generates 1024×1536 (ratio 2:3)
const AI_NATIVE_RATIO = '2:3 (1024×1536)';

/** Translated label for a size preset (falls back to the pt-BR default). */
function getPresetLabel(t: TFunction, id: string): string {
  switch (id) {
    case 'kdp-ebook':
      return t('coverStudio.presetKdpEbookLabel', 'Ebook Amazon KDP');
    case 'ebook-2-3':
      return t('coverStudio.presetEbook23Label', 'Ebook 2:3');
    case 'print-6x9':
      return t('coverStudio.presetPrint6x9Label', 'Print 6×9"');
    case 'print-5x8':
      return t('coverStudio.presetPrint5x8Label', 'Print 5×8"');
    case 'print-5-5x8-5':
      return t('coverStudio.presetPrint55x85Label', 'Print 5.5×8.5"');
    default:
      return id;
  }
}

/** Translated description for a size preset (falls back to the pt-BR default). */
function getPresetDescription(t: TFunction, id: string): string {
  switch (id) {
    case 'kdp-ebook':
      return t('coverStudio.presetKdpEbookDescription', 'Recomendado para Kindle. Ratio 5:8.');
    case 'ebook-2-3':
      return t('coverStudio.presetEbook23Description', 'Proporção clássica. Kobo, Apple Books, Smashwords.');
    case 'print-6x9':
      return t('coverStudio.presetPrint6x9Description', 'Mais popular para impressão. Romance, não-ficção.');
    case 'print-5x8':
      return t('coverStudio.presetPrint5x8Description', 'Formato bolso e YA. Ratio 5:8.');
    case 'print-5-5x8-5':
      return t('coverStudio.presetPrint55x85Description', 'Intermediário. Muito usado em não-ficção.');
    default:
      return '';
  }
}

// ─── Mini proportion rectangle helper ────────────────────────────────────────

interface MiniRectProps {
  width: number;
  height: number;
  maxH?: number;
  minH?: number;
  className?: string;
}

function MiniRect({ width, height, maxH = 28, minH = 16, className }: MiniRectProps) {
  const maxPresetH = Math.max(...SIZE_PRESETS.map((p) => p.height));
  const thumbH = Math.round(minH + (height / maxPresetH) * (maxH - minH));
  const thumbW = Math.round((width / height) * thumbH);

  return (
    <div
      className={cn('rounded-sm border flex-shrink-0', className)}
      style={{ width: thumbW, height: thumbH }}
    />
  );
}

// ─── Lightbox ────────────────────────────────────────────────────────────────

interface LightboxProps {
  imageUrl: string;
  alt?: string;
  onClose: () => void;
  onDownload?: () => void;
}

function Lightbox({ imageUrl, alt, onClose, onDownload }: LightboxProps) {
  const { t } = useTranslation();
  const resolvedAlt = alt ?? t('coverStudio.lightboxAlt', 'Capa');

  // Close on Escape
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        {/* Image container — stop propagation so clicking image doesn't close */}
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={imageUrl}
            alt={resolvedAlt}
            className="max-w-full max-h-[85vh] rounded-xl object-contain shadow-2xl"
          />

          {/* Action bar top-right */}
          <div className="absolute top-2 right-2 flex gap-1.5">
            {onDownload && (
              <button
                type="button"
                onClick={onDownload}
                title={t('coverStudio.downloadImage', 'Baixar imagem')}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 transition-colors"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              title={t('coverStudio.closeEsc', 'Fechar (Esc)')}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-black/60 backdrop-blur-sm text-white hover:bg-black/80 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}

// ─── Download helper ─────────────────────────────────────────────────────────

function downloadDataUrl(dataUrl: string, filename = 'capa.png') {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
}

// ─── Size Select (custom animated dropdown) ───────────────────────────────────

interface SizeSelectProps {
  selectedId: string;
  onSelect: (id: string) => void;
  className?: string;
}

function SizeSelect({ selectedId, onSelect, className }: SizeSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = SIZE_PRESETS.find((p) => p.id === selectedId) ?? SIZE_PRESETS[0];

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleSelect = (id: string) => {
    onSelect(id);
    setOpen(false);
  };

  const ebook = SIZE_PRESETS.filter((p) => p.category === 'ebook');
  const print = SIZE_PRESETS.filter((p) => p.category === 'print');

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('coverStudio.selectSizeAriaLabel', 'Selecionar tamanho de publicação')}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left',
          'transition-all duration-150 select-none',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
          open
            ? 'border-primary bg-primary/5 shadow-sm shadow-primary/10'
            : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30',
        )}
      >
        <div className="flex items-end justify-center flex-shrink-0" style={{ width: 20, height: 20 }}>
          <MiniRect
            width={selected.width}
            height={selected.height}
            maxH={18}
            minH={10}
            className={open ? 'border-primary bg-primary/20' : 'border-muted-foreground/30 bg-muted/60'}
          />
        </div>

        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          <span className="text-xs font-semibold text-foreground truncate">{getPresetLabel(t, selected.id)}</span>
          {selected.recommended && (
            <span className="inline-flex items-center rounded px-1 py-px text-[9px] font-bold uppercase tracking-wider bg-primary text-primary-foreground leading-none flex-shrink-0">
              Amazon
            </span>
          )}
          <span className="text-[10px] font-mono text-muted-foreground ml-auto flex-shrink-0">
            {selected.width}×{selected.height}
            {selected.dpi ? ` · ${selected.dpi}dpi` : ''}
          </span>
        </div>

        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="flex-shrink-0 text-muted-foreground"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </motion.div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="listbox"
            aria-label={t('coverStudio.sizeAriaLabel', 'Tamanho de publicação')}
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              'absolute left-0 right-0 z-50 mt-1 rounded-xl border border-border',
              'bg-popover shadow-xl shadow-ink/15 ring-1 ring-foreground/5',
              'overflow-hidden',
            )}
          >
            <div className="max-h-[240px] overflow-y-auto p-1.5 space-y-1.5">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 px-2 py-0.5">
                  {t('coverStudio.categoryEbook', 'Ebook')}
                </p>
                <div className="space-y-0.5">
                  {ebook.map((preset) => (
                    <SizeSelectItem
                      key={preset.id}
                      preset={preset}
                      isSelected={preset.id === selectedId}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              </div>
              <div className="h-px bg-border/60 mx-1" />
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 px-2 py-0.5">
                  {t('coverStudio.categoryPrint', 'Impressão')}
                </p>
                <div className="space-y-0.5">
                  {print.map((preset) => (
                    <SizeSelectItem
                      key={preset.id}
                      preset={preset}
                      isSelected={preset.id === selectedId}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Size Select Item ─────────────────────────────────────────────────────────

interface SizeSelectItemProps {
  preset: SizePreset;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

function SizeSelectItem({ preset, isSelected, onSelect }: SizeSelectItemProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      onClick={() => onSelect(preset.id)}
      className={cn(
        'group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left',
        'transition-all duration-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset',
        isSelected
          ? 'bg-primary/10 text-foreground'
          : 'text-foreground/80 hover:bg-muted/50 hover:text-foreground',
      )}
    >
      <div className="flex items-end justify-center flex-shrink-0" style={{ width: 20, height: 20 }}>
        <MiniRect
          width={preset.width}
          height={preset.height}
          maxH={18}
          minH={10}
          className={isSelected ? 'border-primary bg-primary/25' : 'border-muted-foreground/30 bg-muted/50 group-hover:border-primary/40'}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-medium leading-tight">{getPresetLabel(t, preset.id)}</span>
          {preset.recommended && (
            <span className="inline-flex items-center rounded px-1 py-px text-[9px] font-bold uppercase tracking-wider bg-primary text-primary-foreground leading-none flex-shrink-0">
              Amazon
            </span>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground leading-snug">{getPresetDescription(t, preset.id)}</p>
      </div>

      <div className="flex-shrink-0 flex items-center gap-1">
        <span className="text-[10px] font-mono text-muted-foreground">{preset.width}×{preset.height}</span>
        {isSelected && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 28 }}
          >
            <CheckCircle2 className="w-3 h-3 text-primary" />
          </motion.div>
        )}
      </div>
    </button>
  );
}

// ─── Shared Cover Preview (prominent, top of layout) ─────────────────────────

interface CoverPreviewProps {
  preset: SizePreset;
  imageUrl?: string;
  onFullscreen?: () => void;
  onClear?: () => void;
}

function CoverPreview({ preset, imageUrl, onFullscreen, onClear }: CoverPreviewProps) {
  const { t } = useTranslation();
  const aspectRatio = preset.width / preset.height;
  // Max preview height: 200px — compact but prominent
  const FRAME_H = 200;
  const frameW = Math.round(aspectRatio * FRAME_H);

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative group" style={{ width: frameW }}>
        <AnimatePresence mode="wait">
          {imageUrl ? (
            <motion.div
              key="with-image"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-xl overflow-hidden shadow-2xl shadow-ink/25 ring-2 ring-primary/30"
              style={{ height: FRAME_H }}
            >
              <img
                src={imageUrl}
                alt={t('coverStudio.previewAlt', 'Preview da capa selecionada')}
                className="w-full h-full object-cover"
              />
              {/* Hover overlay with fullscreen button */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
                {onFullscreen && (
                  <button
                    type="button"
                    onClick={onFullscreen}
                    title={t('coverStudio.fullscreenTitle', 'Ver em tela cheia')}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm text-white hover:bg-black/80"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {/* Dim badge bottom-right */}
              <div className="absolute bottom-1 right-1 bg-black/55 backdrop-blur-sm rounded px-1 py-px">
                <span className="text-[9px] text-white font-mono">{preset.width}×{preset.height}</span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="relative rounded-xl border-2 border-dashed border-border/50 bg-muted/15 flex flex-col items-center justify-center gap-1.5"
              style={{ height: FRAME_H }}
            >
              <div className="w-7 h-7 rounded-full bg-muted/60 flex items-center justify-center">
                <ImageIcon className="w-3.5 h-3.5 text-muted-foreground/40" />
              </div>
              <p className="text-[10px] text-muted-foreground/50 font-medium">{t('coverStudio.previewPlaceholder', 'Preview')}</p>
              <div className="absolute bottom-1 right-1 bg-muted/50 rounded px-1 py-px">
                <span className="text-[9px] text-muted-foreground/50 font-mono">{preset.width}×{preset.height}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Label row */}
      <div className="flex items-center gap-2">
        <p className="text-[10px] font-mono text-muted-foreground">
          {preset.shortLabel}
          {preset.dpi ? ` · ${preset.dpi}dpi` : ''}
        </p>
        {imageUrl && onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] text-destructive/70 hover:text-destructive transition-colors"
          >
            {t('coverStudio.remove', 'remover')}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Thumbnail with actions ───────────────────────────────────────────────────

interface ThumbnailActionProps {
  imageUrl: string;
  index: number;
  isSelected: boolean;
  thumbW: number;
  thumbH: number;
  onSelect: () => void;
  onFullscreen: () => void;
  onDownload: () => void;
}

function ThumbnailWithActions({
  imageUrl,
  index,
  isSelected,
  thumbW,
  thumbH,
  onSelect,
  onFullscreen,
  onDownload,
}: ThumbnailActionProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      key={`cover-${index}`}
      initial={{ opacity: 0, scale: 0.88 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.22, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className={cn('covopt group relative flex-shrink-0', isSelected && 'on')}
      style={{ width: thumbW, height: thumbH }}
    >
      <img
        src={imageUrl}
        alt={t('coverStudio.generatedCoverAlt', { number: index + 1, defaultValue: 'Capa gerada {{number}}' })}
        className="w-full h-full object-cover"
      />

      {/* Number badge top-left */}
      <div className="absolute top-1 left-1 bg-black/55 backdrop-blur-sm rounded px-1 py-px z-10">
        <span className="text-[9px] text-white font-mono font-bold">{index + 1}</span>
      </div>

      {/* Selected indicator (ring + ✓) comes from the .covopt.on CSS rule */}

      {/* Hover overlay with actions */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-colors duration-200 flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
        {/* Fullscreen */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onFullscreen(); }}
          title={t('coverStudio.fullscreenTitle', 'Ver em tela cheia')}
          className="flex items-center justify-center w-7 h-7 rounded-lg bg-white/15 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        {/* Download */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDownload(); }}
          title={t('coverStudio.downloadImage', 'Baixar imagem')}
          className="flex items-center justify-center w-7 h-7 rounded-lg bg-white/15 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
        </button>
        {/* Select / Use */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
          title={isSelected ? t('coverStudio.thumbnailSelectedTitle', 'Selecionada') : t('coverStudio.thumbnailSelectTitle', 'Selecionar capa')}
          className={cn(
            'flex items-center justify-center w-7 h-7 rounded-lg backdrop-blur-sm text-white transition-colors',
            isSelected ? 'bg-primary' : 'bg-white/15 hover:bg-primary',
          )}
        >
          <Check className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

// ─── Upload Tab controls (compact, below preview) ────────────────────────────

interface UploadControlsProps {
  selectedPresetId: string;
  onPresetChange: (id: string) => void;
  onFileChange: (file: File) => void;
  hasImage: boolean;
  onFullscreen: () => void;
}

function UploadControls({ selectedPresetId, onPresetChange, onFileChange, hasImage, onFullscreen }: UploadControlsProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const selectedPreset = SIZE_PRESETS.find((p) => p.id === selectedPresetId) ?? SIZE_PRESETS[0];

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      notify.error(t('coverStudio.invalidImageFile', 'Por favor, envie um arquivo de imagem (JPG, PNG, WebP).'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notify.error(t('coverStudio.imageTooLarge', 'A imagem deve ter no máximo 5 MB.'));
      return;
    }
    onFileChange(file);
  }, [onFileChange, t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  return (
    <div className="space-y-2.5">
      {/* Size select — compact */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold text-muted-foreground whitespace-nowrap">{t('coverStudio.sizeLabel', 'Tamanho')}</span>
        <SizeSelect selectedId={selectedPresetId} onSelect={onPresetChange} className="flex-1" />
        {hasImage && (
          <button
            type="button"
            onClick={onFullscreen}
            title={t('coverStudio.fullscreenTitle', 'Ver em tela cheia')}
            className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Drop zone — compact */}
      <div
        role="button"
        tabIndex={0}
        aria-label={t('coverStudio.uploadAreaAriaLabel', 'Área de upload de imagem')}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
        className={cn(
          'relative flex items-center gap-3 rounded-xl border-2 border-dashed px-4 py-3 cursor-pointer',
          'transition-all duration-200',
          dragOver
            ? 'border-primary bg-primary/8 shadow-md shadow-primary/10'
            : 'border-border/60 bg-muted/10 hover:border-primary/50 hover:bg-muted/20',
        )}
      >
        <motion.div
          animate={{ scale: dragOver ? 1.15 : 1 }}
          transition={{ duration: 0.15 }}
        >
          <Upload className={cn('w-5 h-5 flex-shrink-0 transition-colors', dragOver ? 'text-primary' : 'text-muted-foreground/50')} />
        </motion.div>
        <div>
          <p className={cn('text-xs font-semibold transition-colors', dragOver ? 'text-primary' : 'text-foreground')}>
            {dragOver ? t('coverStudio.dropHere', 'Solte aqui') : t('coverStudio.dragOrClick', 'Arraste ou clique para enviar')}
          </p>
          <p className="text-[10px] text-muted-foreground">{t('coverStudio.fileTypesHint', 'JPG, PNG ou WebP · máx. 5 MB')}</p>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleInputChange} />
      </div>

      {/* Info note — minimal */}
      <div className="flex items-start gap-1.5 rounded-lg bg-primary/5 border border-primary/12 px-2.5 py-1.5 text-[10px] text-muted-foreground">
        <Info className="w-3 h-3 flex-shrink-0 mt-px text-primary/60" />
        <p className="leading-relaxed">
          {t('coverStudio.uploadHintPrefix', 'Envie')}{' '}
          <strong className="text-foreground/80">{selectedPreset.width}×{selectedPreset.height}px</strong>
          {' '}{t('coverStudio.uploadHintSuffix', { ratio: (selectedPreset.width / selectedPreset.height).toFixed(2).replace('.', ','), defaultValue: 'para melhor resultado (proporção {{ratio}}:1).' })}
        </p>
      </div>
    </div>
  );
}

// ─── Generate Tab controls (compact, below preview) ──────────────────────────

interface GenerateControlsProps {
  projectId?: string;
  selectedPresetId: string;
  onPresetChange: (id: string) => void;
  covers: string[];
  selectedIndex: number | null;
  generating: boolean;
  prompt: string;
  onPromptChange: (value: string) => void;
  onGenerate: () => void;
  onSelectCover: (dataUrl: string, index: number) => void;
  onFullscreen: (index: number) => void;
  onDownload: (index: number) => void;
  onClearGenerated?: () => void;
}

function GenerateControls({
  projectId,
  selectedPresetId,
  onPresetChange,
  covers,
  selectedIndex,
  generating,
  prompt,
  onPromptChange,
  onGenerate,
  onSelectCover,
  onFullscreen,
  onDownload,
  onClearGenerated,
}: GenerateControlsProps) {
  const { t } = useTranslation();
  const [describingFromBook, setDescribingFromBook] = useState(false);

  const selectedPreset = SIZE_PRESETS.find((p) => p.id === selectedPresetId) ?? SIZE_PRESETS[0];

  const handleDescribeFromBook = async () => {
    if (!projectId) {
      notify.error(t('coverStudio.noProjectId', 'ID do projeto não disponível.'));
      return;
    }
    setDescribingFromBook(true);
    try {
      const description = await api.generateCoverPrompt(projectId);
      onPromptChange(description);
    } catch (err: unknown) {
      if (err instanceof AiLimitError) {
        notify.aiLimitReached(err.type);
      } else {
        const msg = err instanceof Error ? err.message : t('coverStudio.generateDescriptionError', 'Falha ao gerar descrição');
        notify.error(msg);
      }
    } finally {
      setDescribingFromBook(false);
    }
  };

  // Thumbnail dimensions — based on preset ratio, 80px wide
  const thumbW = 80;
  const thumbH = Math.round(thumbW * (selectedPreset.height / selectedPreset.width));

  return (
    <div className="space-y-2.5">
      {/* Row: size + ai info inline */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold text-muted-foreground whitespace-nowrap">{t('coverStudio.sizeLabel', 'Tamanho')}</span>
        <SizeSelect selectedId={selectedPresetId} onSelect={onPresetChange} className="flex-1" />
      </div>

      {/* AI info compact */}
      <div className="flex items-start gap-1.5 rounded-lg bg-primary/5 border border-primary/12 px-2.5 py-1.5 text-[10px] text-muted-foreground">
        <Sparkles className="w-3 h-3 flex-shrink-0 mt-px text-primary" />
        <p>
          {t('coverStudio.aiGeneratesInPrefix', 'IA gera em')} <strong className="text-foreground/80">{AI_NATIVE_RATIO}</strong> {t('coverStudio.aiGeneratesInSuffix', '— adaptado ao preset escolhido.')}
        </p>
      </div>

      {/* Prompt + describe button */}
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <label className="text-[11px] font-semibold text-foreground">{t('coverStudio.descriptionOptionalLabel', 'Descrição (opcional)')}</label>
          {projectId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDescribeFromBook}
              disabled={describingFromBook || generating}
              className="flex-shrink-0 gap-1 h-6 px-2 text-[10px]"
            >
              {describingFromBook ? (
                <><Loader2 className="w-3 h-3 animate-spin" />{t('coverStudio.analyzing', 'Analisando...')}</>
              ) : (
                <><BookOpen className="w-3 h-3" />{t('coverStudio.describeFromBook', 'Descrever do livro')}</>
              )}
            </Button>
          )}
        </div>

        <div className="relative">
          <Textarea
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder={t('coverStudio.promptPlaceholder', 'Ex: céu estrelado, floresta mística, paleta fria em tons de azul e prata...')}
            rows={2}
            className="resize-none text-xs rounded-[12px] border-0 bg-card ring-1 ring-inset ring-border focus-visible:ring-2 focus-visible:ring-primary/50"
            disabled={generating || describingFromBook}
          />
          {describingFromBook && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/60 backdrop-blur-[2px]">
              <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t('coverStudio.analyzingBook', 'Analisando seu livro...')}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generate / Regenerate button row */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onGenerate}
          disabled={generating || describingFromBook}
          className="btn-ember flex-1"
          style={{ padding: '8px 14px', fontSize: '10px' }}
        >
          {generating ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" />{t('coverStudio.generatingCovers', 'Gerando... (10–30s)')}</>
          ) : (
            <>✦ {covers.length > 0 ? t('coverStudio.regenerate', 'Regenerar') : t('coverStudio.generate4Covers', 'Gerar 4 capas com IA')}</>
          )}
        </button>
        {covers.length > 0 && !generating && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onGenerate}
            disabled={generating}
            title={t('coverStudio.regenerateCoversTitle', 'Regenerar capas')}
            className="h-8 px-2 text-[10px] gap-1 text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <RefreshCw className="w-3 h-3" />
          </Button>
        )}
        {covers.length > 0 && !generating && onClearGenerated && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearGenerated}
            title={t('coverStudio.clearGeneratedTitle', 'Limpar capas geradas')}
            className="h-8 px-2 text-[10px] gap-1 text-destructive/60 hover:text-destructive flex-shrink-0"
          >
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>

      {/* Thumbnails grid — 4 in a row, always rendered */}
      <div className="space-y-1.5">
        <p className={cn('text-[10px] font-semibold transition-colors', covers.length > 0 || generating ? 'text-foreground' : 'text-muted-foreground/40')}>
          {covers.length > 0 ? t('coverStudio.chooseCover', 'Escolha uma capa') : generating ? t('coverStudio.generatingShort', 'Gerando...') : t('coverStudio.coversWillAppear', 'As 4 capas aparecerão aqui')}
        </p>

        <div className="flex gap-2">
          {Array.from({ length: 4 }, (_, i) => {
            const cover = covers[i];
            const isSelected = selectedIndex === i;

            if (cover) {
              return (
                <motion.div
                  key={`cover-${i}`}
                  initial={{ rotateY: 92, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  transition={{ delay: i * 0.14, type: 'spring', stiffness: 160, damping: 17 }}
                  style={{ transformPerspective: 900, transformOrigin: 'left center' }}
                >
                  <ThumbnailWithActions
                    imageUrl={cover}
                    index={i}
                    isSelected={isSelected}
                    thumbW={thumbW}
                    thumbH={thumbH}
                    onSelect={() => onSelectCover(cover, i)}
                    onFullscreen={() => onFullscreen(i)}
                    onDownload={() => onDownload(i)}
                  />
                </motion.div>
              );
            }

            if (generating) {
              return (
                <div
                  key={`shimmer-${i}`}
                  className="rounded-lg border-2 border-border overflow-hidden flex-shrink-0"
                  style={{ width: thumbW, height: thumbH }}
                >
                  <motion.div
                    className="w-full h-full bg-muted/60"
                    animate={{ opacity: [0.3, 0.7, 0.3] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 }}
                  />
                </div>
              );
            }

            return (
              <div
                key={`idle-${i}`}
                className="rounded-lg border-2 border-dashed border-border/35 bg-muted/8 flex flex-col items-center justify-center gap-1 flex-shrink-0"
                style={{ width: thumbW, height: thumbH }}
              >
                <ImageIcon className="w-3.5 h-3.5 text-muted-foreground/20" />
                <span className="text-[9px] font-mono text-muted-foreground/25">{i + 1}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── localStorage persistence helpers ────────────────────────────────────────

interface PersistedGenState {
  covers: string[];
  prompt: string;
  selectedIndex: number | null;
}

function genStorageKey(projectId: string) {
  return `cover-studio:gen:${projectId}`;
}

function loadGenState(projectId: string | undefined): PersistedGenState | null {
  if (!projectId) return null;
  try {
    const raw = localStorage.getItem(genStorageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'covers' in parsed &&
      Array.isArray((parsed as Record<string, unknown>).covers)
    ) {
      return parsed as PersistedGenState;
    }
    return null;
  } catch {
    return null;
  }
}

function saveGenState(projectId: string | undefined, state: PersistedGenState) {
  if (!projectId) return;
  try {
    localStorage.setItem(genStorageKey(projectId), JSON.stringify(state));
  } catch {
    // Ignore quota errors — storage full, silently skip
  }
}

function clearGenState(projectId: string | undefined) {
  if (!projectId) return;
  try {
    localStorage.removeItem(genStorageKey(projectId));
  } catch {
    // ignore
  }
}

// ─── CoverStudio ──────────────────────────────────────────────────────────────

export interface CoverStudioProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Project id for RAG-powered cover description */
  projectId?: string;
  /** Current cover image (data URL) */
  currentCover?: string;
  /** Book metadata for AI generation context */
  bookTitle?: string;
  bookAuthor?: string;
  bookGenre?: string;
  /** Called when user confirms — receives data URL or undefined to remove */
  onConfirm: (dataUrl: string | undefined) => void;
}

export function CoverStudio({
  open,
  onOpenChange,
  projectId,
  currentCover,
  bookTitle,
  bookAuthor,
  bookGenre,
  onConfirm,
}: CoverStudioProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>('upload');
  const [uploadPreview, setUploadPreview] = useState<string | undefined>(currentCover);

  // AI generation state — initialized from localStorage when projectId is available
  const [aiCovers, setAiCovers] = useState<string[]>(() => loadGenState(projectId)?.covers ?? []);
  const [aiSelectedIndex, setAiSelectedIndex] = useState<number | null>(() => loadGenState(projectId)?.selectedIndex ?? null);
  const [aiPrompt, setAiPrompt] = useState<string>(() => loadGenState(projectId)?.prompt ?? '');

  const [selectedPresetId, setSelectedPresetId] = useState('kdp-ebook');
  const [generating, setGenerating] = useState(false);

  // Lightbox state
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxDownloadName, setLightboxDownloadName] = useState('capa.png');

  // Hydrate AI state from localStorage whenever the modal opens or projectId changes
  useEffect(() => {
    if (!open) return;
    const saved = loadGenState(projectId);
    if (saved) {
      setAiCovers(saved.covers);
      setAiSelectedIndex(saved.selectedIndex);
      setAiPrompt(saved.prompt);
    }
  }, [open, projectId]);

  const aiSelectedUrl = aiSelectedIndex !== null ? aiCovers[aiSelectedIndex] : undefined;

  // The "active" preview depends on which tab is open
  const sharedPreviewUrl = activeTab === 'upload' ? uploadPreview : aiSelectedUrl;

  const selectedPreset = SIZE_PRESETS.find((p) => p.id === selectedPresetId) ?? SIZE_PRESETS[0];

  const pendingImage = activeTab === 'upload' ? uploadPreview : aiSelectedUrl;
  const hasSelection = !!pendingImage;

  const handleFileChange = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    // Clear previous batch before regenerating
    setAiCovers([]);
    setAiSelectedIndex(null);
    try {
      // Only pass author if there is a real non-empty name — prevents the AI model
      // from rendering the literal word "Author" on the cover image.
      const realAuthor = bookAuthor?.trim() || undefined;
      const result = await api.generateCover({ prompt: aiPrompt, title: bookTitle, author: realAuthor, genre: bookGenre, projectId });
      setAiCovers(result);
      // Persist the new batch to localStorage
      saveGenState(projectId, { covers: result, prompt: aiPrompt, selectedIndex: null });
    } catch (err: unknown) {
      if (err instanceof AiLimitError) {
        notify.aiLimitReached(err.type);
      } else {
        const msg = err instanceof Error ? err.message : t('coverStudio.generateCoversError', 'Falha ao gerar capas');
        notify.error(msg);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectAiCover = (dataUrl: string, index: number) => {
    setAiSelectedIndex(index);
    // Persist selection to localStorage
    saveGenState(projectId, { covers: aiCovers, prompt: aiPrompt, selectedIndex: index });
    void dataUrl;
  };

  const handleAiPromptChange = (value: string) => {
    setAiPrompt(value);
  };

  const openLightbox = (url: string, name = 'capa.png') => {
    setLightboxUrl(url);
    setLightboxDownloadName(name);
  };

  const handleConfirm = () => {
    onConfirm(pendingImage);
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Do NOT reset aiCovers, aiPrompt or aiSelectedIndex — they persist across open/close
      // Only reset transient UI state
      setUploadPreview(currentCover);
      setGenerating(false);
      setLightboxUrl(null);
    }
    onOpenChange(newOpen);
  };

  const handleClearGenerated = () => {
    setAiCovers([]);
    setAiSelectedIndex(null);
    clearGenState(projectId);
  };

  return (
    <>
      {/* Lightbox — rendered outside modal via portal */}
      {lightboxUrl && (
        <Lightbox
          imageUrl={lightboxUrl}
          alt={t('coverStudio.lightboxFullscreenAlt', 'Capa em tela cheia')}
          onClose={() => setLightboxUrl(null)}
          onDownload={() => downloadDataUrl(lightboxUrl, lightboxDownloadName)}
        />
      )}

      <ResponsiveModal open={open} onOpenChange={handleOpenChange}>
        <ResponsiveModalContent className="sm:max-w-2xl">
          <ResponsiveModalHeader className="pb-0">
            <div className="flex items-center gap-2">
              <ResponsiveModalTitle className="font-serif text-lg tracking-tight leading-none">
                Cover Studio
              </ResponsiveModalTitle>
              <span className="text-[11px] text-muted-foreground hidden sm:inline">
                · {getPresetLabel(t, selectedPreset.id)}
              </span>
            </div>
          </ResponsiveModalHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
            {/* Tab bar — compact */}
            <TabsList className="w-full h-8 rounded-lg p-0.5">
              <TabsTrigger value="upload" className="flex-1 gap-1.5 text-xs font-medium rounded-md">
                <Upload className="w-3 h-3" />
                {t('coverStudio.uploadTab', 'Carregar capa')}
              </TabsTrigger>
              <TabsTrigger value="generate" className="flex-1 gap-1.5 text-xs font-medium rounded-md">
                <Sparkles className="w-3 h-3" />
                {t('coverStudio.generateTab', 'Gerar com IA')}
              </TabsTrigger>
            </TabsList>

            {/* ── SHARED PREVIEW — always visible above tab controls ── */}
            <div className="mt-3 flex justify-center">
              <CoverPreview
                preset={selectedPreset}
                imageUrl={sharedPreviewUrl}
                onFullscreen={sharedPreviewUrl ? () => openLightbox(sharedPreviewUrl, 'capa-preview.png') : undefined}
                onClear={activeTab === 'upload' && uploadPreview ? () => setUploadPreview(undefined) : undefined}
              />
            </div>

            {/* ── TAB CONTROLS (compact, secondary) ── */}
            <div className="mt-3 overflow-y-auto max-h-[38vh]">
              <TabsContent value="upload" className="mt-0">
                <UploadControls
                  selectedPresetId={selectedPresetId}
                  onPresetChange={setSelectedPresetId}
                  onFileChange={handleFileChange}
                  hasImage={!!uploadPreview}
                  onFullscreen={() => uploadPreview && openLightbox(uploadPreview, 'capa-upload.png')}
                />
              </TabsContent>

              <TabsContent value="generate" className="mt-0">
                <GenerateControls
                  projectId={projectId}
                  selectedPresetId={selectedPresetId}
                  onPresetChange={setSelectedPresetId}
                  covers={aiCovers}
                  selectedIndex={aiSelectedIndex}
                  generating={generating}
                  prompt={aiPrompt}
                  onPromptChange={handleAiPromptChange}
                  onGenerate={handleGenerate}
                  onSelectCover={handleSelectAiCover}
                  onFullscreen={(i) => aiCovers[i] && openLightbox(aiCovers[i], `capa-ia-${i + 1}.png`)}
                  onDownload={(i) => aiCovers[i] && downloadDataUrl(aiCovers[i], `capa-ia-${i + 1}.png`)}
                  onClearGenerated={handleClearGenerated}
                />
              </TabsContent>
            </div>
          </Tabs>

          <ResponsiveModalFooter className="pt-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)} className="min-w-[80px] h-8 text-xs">
              {t('coverStudio.cancel', 'Cancelar')}
            </Button>
            <motion.div
              animate={{ scale: hasSelection ? 1 : 0.98, opacity: hasSelection ? 1 : 0.65 }}
              transition={{ duration: 0.15 }}
            >
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!hasSelection}
                className="btn-ember min-w-[130px]"
                style={{ padding: '8px 16px', fontSize: '10px' }}
              >
                {hasSelection ? (
                  <><CheckCircle2 className="w-3.5 h-3.5" />{t('coverStudio.useCover', 'Usar esta capa')}</>
                ) : (
                  <><ImageIcon className="w-3.5 h-3.5 opacity-50" />{t('coverStudio.selectACover', 'Selecione uma capa')}</>
                )}
              </button>
            </motion.div>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </>
  );
}
